//frontend/src/components/Profile.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Button, Row, Col, ProgressBar, Form, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaPassport, FaBriefcase, FaLock } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';

const Profile = () => {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordData, setPasswordData] = useState({
        old_password: '',
        new_password: '',
        confirm_password: '',
    });
    const [loading, setLoading] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [loadingProfile, setLoadingProfile] = useState(true);

    const loadProfile = useCallback(async () => {
        setLoadingProfile(true);
        try {
            const response = await api.get('/user');
            console.log('Profile data loaded successfully');
            setProfileData(response.data.user);
            if (setUser) {
                setUser(response.data.user);
            }
        } catch (error) {
            console.error('Error loading profile:', error);
            toast.error('Erreur lors du chargement du profil');
        } finally {
            setLoadingProfile(false);
        }
    }, [setUser]);

    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    const handlePasswordChange = (e) => {
        setPasswordData({
            ...passwordData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmitPassword = async (e) => {
        e.preventDefault();
        
        if (passwordData.new_password !== passwordData.confirm_password) {
            toast.error('Les nouveaux mots de passe ne correspondent pas');
            return;
        }

        if (passwordData.new_password.length < 8) {
            toast.error('Le mot de passe doit contenir au moins 8 caractères');
            return;
        }

        setLoading(true);
        try {
            await api.post('/user/change-password', passwordData);
            toast.success('✅ Mot de passe modifié avec succès');
            setShowPasswordModal(false);
            setPasswordData({
                old_password: '',
                new_password: '',
                confirm_password: '',
            });
        } catch (err) {
            toast.error(err.response?.data?.error || '❌ Erreur lors du changement de mot de passe');
        } finally {
            setLoading(false);
        }
    };

    if (loadingProfile) {
        return (
            <Container className="py-5 text-center">
                <div className="spinner-border text-primary"></div>
                <p className="mt-3">Chargement du profil...</p>
            </Container>
        );
    }

    const completionPercentage = profileData?.profile_completion_percentage || 0;

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2>Mon Profil</h2>
                    <p className="text-muted">Consultez et modifiez vos informations personnelles</p>
                </Col>
            </Row>

            <Card className="mb-4">
                <Card.Body>
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">Complétion du profil</h5>
                            <span className="badge bg-primary">{completionPercentage}%</span>
                        </div>
                        <ProgressBar 
                            now={completionPercentage} 
                            variant={
                                completionPercentage >= 80 ? 'success' :
                                completionPercentage >= 50 ? 'warning' : 'danger'
                            }
                            style={{ height: '25px' }}
                        />
                        <small className="text-muted d-block mt-2">
                            {completionPercentage >= 80 ? 
                                '✓ Profil complet - Vous pouvez créer un dossier' : 
                                `Complétez encore ${80 - completionPercentage}% pour pouvoir créer un dossier`
                            }
                        </small>
                    </div>
                    <div className="d-flex gap-2">
                        <Button 
                            variant="primary" 
                            onClick={() => navigate('/profile/complete')}
                        >
                            <FaEdit className="me-2" />
                            Modifier mon profil
                        </Button>
                        <Button 
                            variant="warning" 
                            onClick={() => setShowPasswordModal(true)}
                        >
                            <FaLock className="me-2" />
                            Changer le mot de passe
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Header>
                    <FaUser className="me-2" />
                    Informations personnelles
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6} className="mb-3">
                            <strong>Nom complet :</strong>
                            <p className="mb-0">{profileData?.first_name} {profileData?.last_name}</p>
                        </Col>
                        <Col md={6} className="mb-3">
                            <strong><FaEnvelope className="me-2" />Email :</strong>
                            <p className="mb-0">{profileData?.email}</p>
                        </Col>
                        <Col md={6} className="mb-3">
                            <strong><FaPhone className="me-2" />Téléphone :</strong>
                            <p className="mb-0">{profileData?.phone || 'Non renseigné'}</p>
                        </Col>
                        <Col md={6} className="mb-3">
                            <strong>Date de naissance :</strong>
                            <p className="mb-0">{profileData?.date_of_birth || 'Non renseignée'}</p>
                        </Col>
                        <Col md={6} className="mb-3">
                            <strong>Genre :</strong>
                            <p className="mb-0">
                                {profileData?.gender === 'M' ? 'Masculin' : 
                                 profileData?.gender === 'F' ? 'Féminin' : 
                                 profileData?.gender === 'O' ? 'Autre' : 'Non renseigné'}
                            </p>
                        </Col>
                        <Col md={6} className="mb-3">
                            <strong>Nationalité :</strong>
                            <p className="mb-0">{profileData?.nationality || 'Non renseignée'}</p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Header>
                    <FaMapMarkerAlt className="me-2" />
                    Adresse
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={12} className="mb-3">
                            <strong>Adresse complète :</strong>
                            <p className="mb-0">{profileData?.address || 'Non renseignée'}</p>
                        </Col>
                        <Col md={6} className="mb-3">
                            <strong>Ville :</strong>
                            <p className="mb-0">{profileData?.city || 'Non renseignée'}</p>
                        </Col>
                        <Col md={6} className="mb-3">
                            <strong>Pays :</strong>
                            <p className="mb-0">{profileData?.country || 'Non renseigné'}</p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Header>
                    <FaPassport className="me-2" />
                    Informations passeport
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6} className="mb-3">
                            <strong>Numéro de passeport :</strong>
                            <p className="mb-0">{profileData?.passport_number || 'Non renseigné'}</p>
                        </Col>
                        <Col md={6} className="mb-3">
                            <strong>Date d'expiration :</strong>
                            <p className="mb-0">{profileData?.passport_expiry_date || 'Non renseignée'}</p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Header>
                    <FaBriefcase className="me-2" />
                    Informations professionnelles
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6} className="mb-3">
                            <strong>Profession :</strong>
                            <p className="mb-0">{profileData?.current_occupation || 'Non renseignée'}</p>
                        </Col>
                        <Col md={6} className="mb-3">
                            <strong>Employeur :</strong>
                            <p className="mb-0">{profileData?.employer || 'Non renseigné'}</p>
                        </Col>
                        <Col md={6} className="mb-3">
                            <strong>Niveau d'études :</strong>
                            <p className="mb-0">{profileData?.education_level || 'Non renseigné'}</p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Changer le mot de passe</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmitPassword}>
                        <Form.Group className="mb-3">
                            <Form.Label>Ancien mot de passe *</Form.Label>
                            <Form.Control
                                type="password"
                                name="old_password"
                                value={passwordData.old_password}
                                onChange={handlePasswordChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Nouveau mot de passe *</Form.Label>
                            <Form.Control
                                type="password"
                                name="new_password"
                                value={passwordData.new_password}
                                onChange={handlePasswordChange}
                                required
                                minLength={8}
                            />
                            <Form.Text className="text-muted">
                                Minimum 8 caractères
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Confirmer le nouveau mot de passe *</Form.Label>
                            <Form.Control
                                type="password"
                                name="confirm_password"
                                value={passwordData.confirm_password}
                                onChange={handlePasswordChange}
                                required
                            />
                        </Form.Group>

                        <div className="d-flex gap-2">
                            <Button 
                                type="submit" 
                                variant="primary" 
                                disabled={loading}
                                className="flex-fill"
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Modification...
                                    </>
                                ) : (
                                    'Changer le mot de passe'
                                )}
                            </Button>
                            <Button 
                                variant="secondary" 
                                onClick={() => setShowPasswordModal(false)}
                                className="flex-fill"
                            >
                                Annuler
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default Profile;